import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
const API_URL = process.env.REACT_APP_API_URL;
const RTIAdd = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = !!id;

  const [form, setForm] = useState({
    receipt_date: new Date().toISOString().split("T")[0],
    amount: "",
    amount_words: "",
    payee_name: "",
    payment_mode: "cash",
    reference_no: "",
    reference_date: "",
    bank_name: "",
    branch_name: "",
    remark: "",
  });

  const [banks, setBanks] = useState([]);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [bankSearch, setBankSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  /* ================= POPULATE FORM HELPER ================= */
  const populateForm = (record) => {
    setForm({
      receipt_date: record.receipt_date?.slice(0, 10) || new Date().toISOString().split("T")[0],
      amount: record.amount || "",
      amount_words: record.amount_words || (record.amount ? numberToWords(record.amount) : ""),
      payee_name: record.payee_name || "",
      payment_mode: record.payment_mode || "cash",
      reference_no: record.reference_no || "",
      reference_date: record.reference_date?.slice(0, 10) || "",
      bank_name: record.bank_name || "",
      branch_name: record.branch_name || "",
      remark: record.remark || "",
    });
    setBankSearch(record.bank_name || "");
  };

  /* ================= PRE-FILL FOR EDIT ================= */
  useEffect(() => {
    if (!isEdit) return;

    const loadRecord = async () => {
      setLoading(true);

      // 1. Try location state first (best for navigation)
      if (location.state?.record) {
        console.log("Loading from navigation state");
        populateForm(location.state.record);
        setLoading(false);
        return;
      }

      // 2. Fallback: Fetch from API
      try {
        console.log(`Fetching RTI record ID: ${id}`);
        const res = await fetch(`${API_URL}/api/rti/${id}`);

        if (!res.ok) throw new Error("Failed to fetch record");

        const record = await res.json();
        populateForm(record);
      } catch (err) {
        console.error(err);
        setErrors({ general: "Failed to load receipt data. Please try again." });
      } finally {
        setLoading(false);
      }
    };

    loadRecord();
  }, [isEdit, id]);

  /* ================= FETCH BANKS ================= */
  useEffect(() => {
    fetch(`${API_URL}/api/banks`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) setBanks(data);
        else if (Array.isArray(data.banks)) setBanks(data.banks);
        else setBanks([]);
      })
      .catch((err) => console.error("Failed to fetch banks", err));
  }, []);

  /* ================= NUMBER TO WORDS ================= */
  const numberToWords = (num) => {
    if (!num) return "";
    num = parseFloat(num);
    const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    const inWords = (n) => {
      if (n < 20) return a[n];
      if (n < 100) return b[Math.floor(n / 10)] + " " + a[n % 10];
      if (n < 1000) return a[Math.floor(n / 100)] + " Hundred " + inWords(n % 100);
      if (n < 100000) return inWords(Math.floor(n / 1000)) + " Thousand " + inWords(n % 1000);
      if (n < 10000000) return inWords(Math.floor(n / 100000)) + " Lakh " + inWords(n % 100000);
      return inWords(Math.floor(n / 10000000)) + " Crore " + inWords(n % 10000000);
    };

    return inWords(Math.floor(num)) + " Rupees Only";
  };

  /* ================= HANDLE CHANGE ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    let updated = { ...form, [name]: value };

    if (name === "amount") {
      updated.amount_words = !isNaN(value) && value !== "" ? numberToWords(value) : "";
    }

    setForm(updated);

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  /* ================= VALIDATION ================= */
  const validate = () => {
    let err = {};
    if (!form.payee_name?.trim() || form.payee_name.trim().length < 2) {
      err.payee_name = "Payee name must be at least 2 characters";
    }
    if (!form.amount || parseFloat(form.amount) <= 0) {
      err.amount = "Enter valid amount";
    }
    if (!form.reference_no?.trim()) {
      err.reference_no = "Reference number required";
    }
    if (!form.reference_date) {
      err.reference_date = "Reference date required";
    }
    return err;
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (Object.keys(err).length > 0) {
      setErrors(err);
      return;
    }

    try {
      const url = isEdit
        ? `${API_URL}/api/rti/${id}`
        : `${API_URL}/api/rti`;

      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setErrors({ general: data.detail || "Something went wrong" });
        return;
      }

      setSuccessMessage(
        isEdit
          ? "Receipt updated successfully ✅"
          : `Receipt ${data.receipt_no || ""} created successfully ✅`
      );

      setTimeout(() => navigate("/rti-list"), 1500);
    } catch (err) {
      setErrors({ general: "Server error. Please try again." });
    }
  };

  if (loading) {
    return (
      <div className="pt-6 max-w-5xl mx-auto text-center">
        <p className="text-lg">Loading receipt data...</p>
      </div>
    );
  }

  return (
    <div className="pt-6 pb-8 max-w-5xl mx-auto">
      {/* HEADER */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-2xl p-6 shadow mb-6">
        <h2 className="text-2xl font-semibold">
          {isEdit ? "Edit RTI Receipt" : "New RTI Receipt"}
        </h2>
        <p className="text-sm opacity-90 mt-1">
          {isEdit ? "Update existing receipt" : "Create a new RTI receipt entry"}
        </p>
      </div>

      {/* GENERAL ERROR */}
      {errors.general && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl text-sm mb-6">
          {errors.general}
        </div>
      )}

      {/* FORM */}
      <div className="bg-white rounded-2xl shadow p-8">
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input label="Receipt Date" type="date" name="receipt_date" value={form.receipt_date} onChange={handleChange} />

<Input label="Amount (₹)" name="amount" type="number" value={form.amount} onChange={handleChange} error={errors.amount} required />
          <Input label="Amount in Words" value={form.amount_words} disabled />

<Input label="Payee Name" name="payee_name" value={form.payee_name} onChange={handleChange} error={errors.payee_name} required />

          <Select name="payment_mode" value={form.payment_mode} onChange={handleChange} error={errors.payment_mode} required />

        <Input label="Reference No" name="reference_no" value={form.reference_no} onChange={handleChange} error={errors.reference_no} required />
<Input label="Reference Date" type="date" name="reference_date" value={form.reference_date} onChange={handleChange} error={errors.reference_date} required />

          {/* Bank Dropdown */}
          <div className="space-y-1.5 relative">
            <label className="text-sm font-medium text-gray-700">Bank Name</label>
            <input
              type="text"
              name="bank_name"
              value={form.bank_name}
              onChange={(e) => {
                handleChange(e);
                setBankSearch(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
              placeholder="Select or type bank name"
              className="w-full px-4 py-2.5 rounded-xl border bg-gray-50 focus:ring-2 focus:ring-blue-500"
            />

            {showDropdown && banks.length > 0 && (
              <div className="absolute z-50 w-full bg-white border rounded-xl shadow max-h-48 overflow-y-auto mt-1">
                {banks
                  .filter((bank) => bank.toLowerCase().includes((bankSearch || "").toLowerCase()))
                  .map((bank, i) => (
                    <div
                      key={i}
                      onClick={() => {
                        setForm((prev) => ({ ...prev, bank_name: bank }));
                        setBankSearch(bank);
                        setShowDropdown(false);
                      }}
                      className="px-4 py-2.5 hover:bg-gray-100 cursor-pointer"
                    >
                      {bank}
                    </div>
                  ))}
              </div>
            )}
          </div>

          <Input label="Branch Name" name="branch_name" value={form.branch_name} onChange={handleChange} />
          <Input label="Remark" name="remark" value={form.remark} onChange={handleChange} />

          {/* ACTION BUTTONS */}
          <div className="md:col-span-2 flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate("/rti-list")}
              className="px-8 py-3.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-12 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium shadow transition"
            >
              {isEdit ? "Update Receipt" : "Save Receipt"}
            </button>
          </div>
        </form>
      </div>

      {/* SUCCESS MODAL */}
      {successMessage && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-8 text-center">
            <div className="text-5xl mb-4">✅</div>
            <h2 className="text-xl font-semibold mb-2">Success</h2>
            <p className="text-gray-600 mb-6">{successMessage}</p>
            <button
              onClick={() => setSuccessMessage("")}
              className="px-8 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ================= REUSABLE COMPONENTS ================= */
const Input = ({ label, error, required, ...props }) => (
  <div className="space-y-1.5">
    <label className="text-sm font-medium text-gray-700">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      {...props}
      className={`w-full px-4 py-2.5 rounded-xl border bg-gray-50 focus:ring-2 focus:ring-blue-500 transition
        ${error ? "border-red-500" : "border-gray-300"}
        ${props.disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    />
    {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
  </div>
);

const Select = ({ ...props }) => (
  <div className="space-y-1.5">
    <label className="text-sm font-medium text-gray-700">Payment Mode</label>
    <select
      {...props}
      className="w-full px-4 py-2.5 rounded-xl border bg-gray-50 focus:ring-2 focus:ring-blue-500"
    >
      <option value="cash">Cash</option>
      <option value="neft">NEFT</option>
      <option value="dd">DD</option>
      <option value="cheque">Cheque</option>
    </select>
  </div>
);

export default RTIAdd;